import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
  type ReactNode,
} from 'react';
import type { BrandPalette, ConstructorValue, Typography } from '../types';

export interface ConstructorState {
  value: ConstructorValue;
  past: ConstructorValue[];
  future: ConstructorValue[];
}

type Action =
  | { type: 'SET_PALETTE_ROLE'; role: keyof BrandPalette; value: string | undefined }
  | {
      type: 'SET_OVERRIDE';
      componentName: string;
      knobName: string;
      value: string | number | undefined;
    }
  | {
      type: 'SET_TYPOGRAPHY';
      key: keyof Typography;
      value: string | number | undefined;
    }
  | { type: 'LOAD'; value: ConstructorValue }
  | { type: 'RESET' }
  | { type: 'UNDO' }
  | { type: 'REDO' };

const EMPTY: ConstructorValue = { version: 2 };

function applyPaletteRole(
  value: ConstructorValue,
  role: keyof BrandPalette,
  next: string | undefined,
): ConstructorValue {
  const palette = { ...(value.palette ?? {}) };
  if (next === undefined) delete palette[role];
  else palette[role] = next;
  const result: ConstructorValue = { ...value };
  if (Object.keys(palette).length > 0) {
    result.palette = palette;
  } else {
    delete result.palette;
  }
  return result;
}

function applyOverride(
  value: ConstructorValue,
  componentName: string,
  knobName: string,
  next: string | number | undefined,
): ConstructorValue {
  const overrides = { ...(value.overrides ?? {}) };
  const knobMap = { ...(overrides[componentName] ?? {}) };
  if (next === undefined) {
    delete knobMap[knobName];
  } else {
    knobMap[knobName] = next;
  }
  if (Object.keys(knobMap).length > 0) {
    overrides[componentName] = knobMap;
  } else {
    delete overrides[componentName];
  }
  const result: ConstructorValue = { ...value };
  if (Object.keys(overrides).length > 0) {
    result.overrides = overrides;
  } else {
    delete result.overrides;
  }
  return result;
}

function applyTypographyKey(
  value: ConstructorValue,
  key: keyof Typography,
  next: string | number | undefined,
): ConstructorValue {
  const typography = { ...(value.typography ?? {}) } as Record<string, unknown>;
  if (next === undefined) delete typography[key];
  else typography[key] = next;
  const result: ConstructorValue = { ...value };
  if (Object.keys(typography).length > 0) {
    result.typography = typography as Typography;
  } else {
    delete result.typography;
  }
  return result;
}

function pushHistory(state: ConstructorState, next: ConstructorValue): ConstructorState {
  return {
    value: next,
    past: [...state.past, state.value],
    future: [],
  };
}

function reducer(state: ConstructorState, action: Action): ConstructorState {
  switch (action.type) {
    case 'SET_PALETTE_ROLE': {
      const next = applyPaletteRole(state.value, action.role, action.value);
      return pushHistory(state, next);
    }
    case 'SET_OVERRIDE': {
      const next = applyOverride(
        state.value,
        action.componentName,
        action.knobName,
        action.value,
      );
      return pushHistory(state, next);
    }
    case 'SET_TYPOGRAPHY': {
      const next = applyTypographyKey(state.value, action.key, action.value);
      return pushHistory(state, next);
    }
    case 'LOAD':
      return pushHistory(state, action.value);
    case 'RESET':
      return pushHistory(state, EMPTY);
    case 'UNDO': {
      if (state.past.length === 0) return state;
      const previous = state.past[state.past.length - 1];
      if (previous === undefined) return state;
      return {
        value: previous,
        past: state.past.slice(0, -1),
        future: [state.value, ...state.future],
      };
    }
    case 'REDO': {
      if (state.future.length === 0) return state;
      const next = state.future[0];
      if (next === undefined) return state;
      return {
        value: next,
        past: [...state.past, state.value],
        future: state.future.slice(1),
      };
    }
    default:
      return state;
  }
}

export interface UseConstructorReturn {
  state: ConstructorState;
  canUndo: boolean;
  canRedo: boolean;
  setPaletteRole(role: keyof BrandPalette, value: string | undefined): void;
  setOverride(
    componentName: string,
    knobName: string,
    value: string | number | undefined,
  ): void;
  setTypography(key: keyof Typography, value: string | number | undefined): void;
  load(value: ConstructorValue): void;
  reset(): void;
  undo(): void;
  redo(): void;
}

const Context = createContext<UseConstructorReturn | null>(null);

export function ConstructorProvider({
  initialValue,
  children,
}: {
  initialValue?: ConstructorValue;
  children: ReactNode;
}) {
  const [state, dispatch] = useReducer(reducer, {
    value: initialValue ?? EMPTY,
    past: [],
    future: [],
  });

  const setPaletteRole = useCallback(
    (role: keyof BrandPalette, value: string | undefined) =>
      dispatch({ type: 'SET_PALETTE_ROLE', role, value }),
    [],
  );
  const setOverride = useCallback(
    (componentName: string, knobName: string, value: string | number | undefined) =>
      dispatch({ type: 'SET_OVERRIDE', componentName, knobName, value }),
    [],
  );
  const setTypography = useCallback(
    (key: keyof Typography, value: string | number | undefined) =>
      dispatch({ type: 'SET_TYPOGRAPHY', key, value }),
    [],
  );
  const load = useCallback(
    (value: ConstructorValue) => dispatch({ type: 'LOAD', value }),
    [],
  );
  const reset = useCallback(() => dispatch({ type: 'RESET' }), []);
  const undo = useCallback(() => dispatch({ type: 'UNDO' }), []);
  const redo = useCallback(() => dispatch({ type: 'REDO' }), []);

  const ctx = useMemo<UseConstructorReturn>(
    () => ({
      state,
      canUndo: state.past.length > 0,
      canRedo: state.future.length > 0,
      setPaletteRole,
      setOverride,
      setTypography,
      load,
      reset,
      undo,
      redo,
    }),
    [state, setPaletteRole, setOverride, setTypography, load, reset, undo, redo],
  );

  return <Context.Provider value={ctx}>{children}</Context.Provider>;
}

export function useConstructor(): UseConstructorReturn {
  const ctx = useContext(Context);
  if (!ctx) throw new Error('useConstructor must be used inside ConstructorProvider');
  return ctx;
}
